import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor
} from '@angular/common/http';
import { EMPTY, iif, Observable, of, throwError } from 'rxjs';
import { SessionQuery } from '../state/session/session.query';
import {
    catchError,
    filter,
    map,
    mergeMap,
    switchMap,
    switchMapTo,
    take,
    withLatestFrom
} from 'rxjs/operators';
import { AppConfigQuery } from '../state/app-config/app-config.query';
import { KeycloakService } from 'keycloak-angular';
import { ImpersonateQuery } from '../../pages/admin/impersonate/state/impersonate.query';

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
    constructor(
        private appConfig: AppConfigQuery,
        private sessionQuery: SessionQuery,
        protected readonly keycloak: KeycloakService,
        private impersonateQuery: ImpersonateQuery
    ) {}

    /**
     * Paths that do not require (or will not function with) access token to be included
     */
    private excludeUrls = ['oauth/token', '/password/'];

    intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        if (this.excludeBearerToken(request)) {
            return next.handle(request);
        }

        return this.sessionQuery.isLoading$.pipe(
            take(1),
            // check if we have a pending refresh token request...
            mergeMap((isLoading) =>
                iif(
                    () => isLoading,
                    // ...and wait until it's finished before continuing...
                    this.sessionQuery.isLoading$.pipe(filter((isLoading) => !isLoading)),
                    of(false)
                )
            ),
            switchMapTo(
                this.sessionQuery.token$.pipe(
                    withLatestFrom(this.impersonateQuery.token$),
                    take(1),
                    map(([token, impersonationToken]) => {
                        const bearerToken = impersonationToken || token;
                        return request.clone({
                            setHeaders: {
                                Authorization:
                                    // use manually set Authorization header if present
                                    request.headers.get('Authorization') ||
                                    `Bearer ${bearerToken}`
                            }
                        });
                    }),
                    switchMap((newReq) => next.handle(newReq)),
                    catchError((error) => {
                        if (error.error.status === 401) {
                            /* We had to disable logout on 401 becuase of Meriti and similar
                            as they are not using all features/modules and some are returning 401 */
                            //TODO: create a flag for making 401 log out users
                            // this.session.logout('invalidAccessToken');

                            // No point doing anything further in subscribers when logging out.
                            return EMPTY;
                        }
                        return throwError(error);
                    })
                )
            )
        );
    }

    excludeBearerToken(request: HttpRequest<any>): boolean {
        return (
            !this.isRequestToTrustedBackend(request) ||
            !!this.excludeUrls.find((e) => request.url.includes(e))
        );
    }

    isRequestToTrustedBackend(request: HttpRequest<any>): boolean {
        const gwUrl = this.appConfig.get().gatewayBaseUrl;
        const lcUrl = this.appConfig.get().layoutConfigurationBaseUrl;
        const fcUrl = this.appConfig.get().formConfigBaseUrl;
        const wUrl = this.appConfig.get().widgetDataBaseUrl;
        const pefUrl = this.appConfig.get().peFundBaseUrl;
        const paUrl = this.appConfig.get().privateAssetBaseUrl;
        const caUrl = this.appConfig.get().categoriesBaseUrl;
        const docUrl = this.appConfig.get().documentsBaseUrl;
        const adUrl = this.appConfig.get().adminBaseUrl;
        const kcUrl = this.appConfig.get().keycloakUrl;
        const globUrl = this.appConfig.get().globalResourceBaseUrl;
        const adminUrl = this.appConfig.get().adminBaseUrl;
        const accUrl = this.appConfig.get().accountingBaseUrl;
        const consumerBaseUrl = this.appConfig.get().consumerBaseUrl;
        const clickhouseBaseUrl = this.appConfig.get().clickhouseBaseUrl;
        const sharedResourceBaseUrl = this.appConfig.get().sharedResourceBaseUrl;
        return (
            request.url.includes(gwUrl) ||
            request.url.includes(lcUrl) ||
            request.url.includes(fcUrl) ||
            request.url.includes(wUrl) ||
            request.url.includes(pefUrl) ||
            request.url.includes(paUrl) ||
            request.url.includes(adUrl) ||
            request.url.includes(caUrl) ||
            request.url.includes(docUrl) ||
            request.url.includes(kcUrl) ||
            request.url.includes(adminUrl) ||
            request.url.includes(accUrl) ||
            request.url.includes(globUrl) ||
            request.url.includes(consumerBaseUrl) ||
            request.url.includes(clickhouseBaseUrl) ||
            request.url.includes(sharedResourceBaseUrl)
        );
    }
}
